import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { FilterKeyType } from '@ocean/types/dist/filter'
import { CommonUtils, PaginationUtils } from '@ocean/utils'
import { Card } from 'types/card'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { CardUtils } from 'utils'
import { userLoggedOut } from 'core/session/slices/session'
import {
  getProficiencies,
  getGroup,
} from 'modules/requirements/Requirements/api/requirementsService'
import {
  RequirementsFilterItems,
  RequirementsFilterItem,
} from 'modules/requirements/Requirements/types'
import { RequirementsState, ProficienciesListResult, RequirementsGroupResult } from './types'

const initialState: RequirementsState = {
  proficienciesList: {},
  requirementsGroup: {},
  requirementsFilters: {
    requirementStatus: { keyType: FilterKeyType.Number },
    criticalForRank: { keyType: FilterKeyType.Boolean },
    readyToBeAssessed: { keyType: FilterKeyType.Boolean },
    activityType: { keyType: FilterKeyType.Array },
    availability: { keyType: FilterKeyType.Boolean },
  },
}

const requirementsSlice = createSlice({
  name: 'requirements',
  initialState,
  reducers: {
    proficienciesDataLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ProficienciesListResult>>
    ) {
      state.proficienciesList = action.payload
    },
    clearProficienciesList(state) {
      state.proficienciesList = initialState.proficienciesList
    },
    requirementsGroupDataLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<RequirementsGroupResult>>
    ) {
      state.requirementsGroup = action.payload
    },
    clearRequirementsGroup(state) {
      state.requirementsGroup = initialState.requirementsGroup
    },
    setRequirementsFilters(state, action: PayloadAction<RequirementsFilterItem>) {
      const filter = state.requirementsFilters[action.payload.name]
      if (filter) filter.value = action.payload.value
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  proficienciesDataLoaded,
  requirementsGroupDataLoaded,
  setRequirementsFilters,
  clearProficienciesList,
  clearRequirementsGroup,
} = requirementsSlice.actions

export { setRequirementsFilters, clearProficienciesList, clearRequirementsGroup }
export default requirementsSlice.reducer

const handleProficienciesResponse =
  (response: ApiMiddlewarePayload<ProficienciesListResult>): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (!error && result?.proficienciesData) {
      const hasImages = CardUtils.checkProficiencyCardsHasImages(result.proficienciesData)
      dispatch(
        proficienciesDataLoaded({
          ...response,
          result: {
            ...result,
            hasImages,
          },
        })
      )
    } else {
      dispatch(proficienciesDataLoaded(response))
    }
  }

export const getProficienciesList = (externalFilters: RequirementsFilterItems | {}) => {
  return createApiAction({
    apiCalls: {
      proficienciesData: () => getProficiencies(externalFilters),
    },
    payloadAction: proficienciesDataLoaded,
    nextAction: handleProficienciesResponse,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ requirements }) => requirements.proficienciesList.result,
  })
}

export const getRequirementsCardsPartition = createSelector(
  (requirementsCards: Card[] = []) => requirementsCards,
  requirementsCards =>
    CommonUtils.partition<Card>(requirementsCards, CardUtils.checkRequirementsGroupCardType)
)

export const getRequirementsPageItems = createSelector(
  (requirementsCards: Card[] = [], currentPageId: number, currentPageSize: number) => ({
    requirementsCards: getRequirementsCardsPartition(requirementsCards),
    currentPageId,
    currentPageSize,
  }),
  ({ requirementsCards, currentPageId, currentPageSize }) => {
    const [, requirements] = requirementsCards
    return PaginationUtils.getPageItems<Card>(requirements, currentPageId, currentPageSize)
  }
)

const handleRequirementsGroupResponse =
  (response: ApiMiddlewarePayload<RequirementsGroupResult>): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (!error && result?.requirementsGroupData?.content) {
      const hasImages = CardUtils.checkProficiencyCardsHasImages(
        result.requirementsGroupData.content
      )
      dispatch(
        requirementsGroupDataLoaded({
          ...response,
          result: {
            ...result,
            hasImages,
          },
        })
      )
    } else {
      dispatch(requirementsGroupDataLoaded(response))
    }
  }

export const getRequirementsGroup = (groupId: Guid, externalFilters: RequirementsFilterItems) => {
  return createApiAction({
    apiCalls: {
      requirementsGroupData: () => getGroup(groupId, externalFilters),
    },
    payloadAction: requirementsGroupDataLoaded,
    nextAction: handleRequirementsGroupResponse,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ requirements }) => requirements.requirementsGroup.result,
  })
}
